"use client";

import { useEffect, useRef, useState } from "react";
import { HERO_PILLS, HERO_ROTATIONS, WHATSAPP_URL } from "@/lib/site";
import { media } from "@/lib/media";
import AutoVideo from "@/components/ux/auto-video";

const ROTATE_MS = 4600;

const STATS = [
  { value: 500, prefix: "", suffix: "+", label: "clientes premium", decimals: 0 },
  { value: 900, prefix: "R$ ", suffix: " mi", label: "em GMV por ano", decimals: 0 },
  { value: 15, prefix: "", suffix: " anos", label: "de mercado", decimals: 0 },
  { value: 99.999, prefix: "", suffix: "%", label: "de uptime", decimals: 3 },
];

/* O HTML sai com o valor final (é o que buscadores e IAs leem); no navegador
   o número fica oculto até a contagem começar do zero. */
function useCountUp(target: number, run: boolean, decimals = 0) {
  const [val, setVal] = useState(target);
  const [counting, setCounting] = useState(false);
  useEffect(() => {
    if (!run) return;
    const dur = 1600;
    const t0 = performance.now();
    let raf: number;
    const tick = (t: number) => {
      const p = Math.min((t - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setCounting(true);
      setVal(parseFloat((target * eased).toFixed(decimals)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run, target, decimals]);
  return { val, counting };
}

function Stat({ value, prefix, suffix, label, decimals, run }: (typeof STATS)[number] & { run: boolean }) {
  const { val, counting } = useCountUp(value, run, decimals);
  return (
    <div className="py-6 pl-5 lg:py-7 lg:pl-8">
      <div className={`font-display text-3xl font-bold tracking-tight text-paper transition-opacity duration-300 lg:text-[2.6rem] ${counting ? "" : "opacity-0"}`}>
        {prefix}
        {val.toLocaleString("pt-BR", {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })}
        <span className="text-lime">{suffix}</span>
      </div>
      <div className="mt-1 text-[13px] text-smoke-dark lg:text-sm">{label}</div>
    </div>
  );
}

/* frase ativa animada palavra a palavra sob máscara */
function MaskedWords({ text, className = "" }: { text: string; className?: string }) {
  return (
    <>
      {text.split(" ").map((w, i) => (
        <span key={`${text}-${i}`} className="word-mask">
          <span className="word-in" style={{ animationDelay: `${0.06 * i}s` }}>
            {w}
          </span>
          {i < text.split(" ").length - 1 ? " " : ""}
        </span>
      ))}
    </>
  );
}

export default function Hero() {
  const [idx, setIdx] = useState(0);
  const [run, setRun] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_ROTATIONS.length), ROTATE_MS);
    const r = setTimeout(() => setRun(true), 500);
    return () => {
      clearInterval(t);
      clearTimeout(r);
    };
  }, []);


  const active = HERO_ROTATIONS[idx];
  const subRef = useRef<HTMLParagraphElement>(null);
  const primeiraFrase = useRef(true);
  useEffect(() => {
    if (primeiraFrase.current) {
      primeiraFrase.current = false;
      return;
    }
    subRef.current?.animate(
      [
        { opacity: 0, transform: "translateY(40%)" },
        { opacity: 1, transform: "none" },
      ],
      { duration: 650, delay: 250, easing: "cubic-bezier(0.22, 1, 0.36, 1)", fill: "backwards" }
    );
  }, [idx]);

  return (
    <section className="relative -mt-16 flex min-h-svh flex-col overflow-hidden bg-ink text-paper lg:-mt-20">
      {/* vídeo manifesto como matéria de fundo — P&B, atrás de véu escuro */}
      <AutoVideo
        acimaDaDobra
        src={media("/media/manifesto-720.mp4")}
        poster="/media/manifesto-poster.webp"
        className="absolute inset-0 h-full w-full scale-105 object-cover opacity-55 grayscale"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/65 via-ink/20 to-ink" />
      <div className="glow-lime absolute inset-0" />

      <div className="relative mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-5 pb-14 pt-32 lg:px-10 lg:pt-40">
        <p className="mb-7 inline-flex w-fit items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-4 py-2 backdrop-blur">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-lime opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-lime" />
          </span>
          <span className="text-[13px] font-semibold tracking-wide text-paper/80">
            15 anos de e-commerce · mais de 500 marcas premium
          </span>
        </p>

        <h1 className="max-w-5xl text-[clamp(2.6rem,7.2vw,5.9rem)] font-bold leading-[0.98]">
          Plataforma de <span className="whitespace-nowrap">e-commerce</span>{" "}
          <span className="relative mt-1 block text-lime">
            {/* sizers invisíveis reservam a altura da frase mais alta */}
            {/* texto via CSS (attr): reserva a altura sem entrar no H1 que o Google lê */}
            <span className="invisible grid" aria-hidden>
              {HERO_ROTATIONS.map((r) => (
                <span key={r.highlight} data-t={r.highlight} className="col-start-1 row-start-1 before:content-[attr(data-t)]" />
              ))}
            </span>
            <span key={active.highlight} className="absolute inset-x-0 top-0">
              <MaskedWords text={active.highlight} />
            </span>
          </span>
        </h1>

        <div className="relative mt-7 max-w-xl">
          <p
            className="invisible text-lg leading-relaxed before:content-[attr(data-t)] lg:text-xl"
            data-t={HERO_ROTATIONS.reduce((a, b) => (a.sub.length > b.sub.length ? a : b)).sub}
            aria-hidden
          />
          {/* mesmo elemento em todas as trocas: um <p> novo a cada frase viraria
              um novo candidato a LCP e empurraria a métrica para 7s+ */}
          <p ref={subRef} className="absolute inset-x-0 top-0 text-lg leading-relaxed text-paper/65 lg:text-xl">
            {active.sub}
          </p>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 rounded-full bg-lime py-2 pl-7 pr-2 text-base font-bold text-ink transition hover:bg-lime-deep"
          >
            Falar com um especialista
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ink text-lime transition-transform duration-300 group-hover:rotate-45">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7M9 7h8v8" />
              </svg>
            </span>
          </a>
          <a
            href="/plataforma"
            className="rounded-full border border-white/20 px-7 py-4 text-base font-semibold text-paper/85 transition hover:border-paper hover:text-paper"
          >
            Conhecer a plataforma
          </a>
        </div>
      </div>

      {/* números — régua corporativa colada na base */}
      <div className="relative border-t border-white/10 bg-ink/40 backdrop-blur-sm">
        <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-white/10 lg:grid-cols-4">
          {STATS.map((s) => (
            <Stat key={s.label} {...s} run={run} />
          ))}
        </div>
      </div>

      {/* ticker de highlights */}
      <div className="relative overflow-hidden border-t border-white/10 py-4">
        <div className="marquee-track slow flex w-max items-center">
          {[...HERO_PILLS, ...HERO_PILLS].map((p, i) => (
            <span key={`${p}-${i}`} className="flex items-center whitespace-nowrap text-[13px] font-semibold uppercase tracking-[0.18em] text-paper/50">
              <span className="px-6">{p}</span>
              <span className="h-1 w-1 rounded-full bg-lime" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
